import type {
  CellClassParams,
  EditableCallbackParams,
  RowClassParams,
} from 'ag-grid-community'
import type { OptionRow } from '@/services/property/property.type'
import { canEditSharedOptionDraft } from '@/services/property/option.access'
import {
  resolveActiveOrganizationId,
  resolveInheritedOrganizationIds,
} from '@/utils/organization'
import { isRecord } from '@/utils/typeGuards'

type OptionPermissions = {
  isGuest: boolean
  isOwner: boolean
  isSameOrganization: boolean
  isListOwner: boolean
  isListOwnerResolved: boolean
  isNewOption: boolean
  isGroupHeader: boolean
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canEditExisting: boolean
  canDeleteExisting: boolean
  canUnarchive: boolean
}

export type OptionListRestrictionReason =
  | 'missing-update-right'
  | 'owner-unresolved'
  | 'limited-row-scope'
  | 'not-list-owner'

export type OptionListAccess = {
  canManageList: boolean
  restrictionReason: OptionListRestrictionReason | null
}

type OptionPermissionInput = {
  context: unknown
  node?: {
    group?: boolean
    data?: unknown
  } | null
  data?: unknown
}

type OptionRowLike = {
  id?: string
  userID?: string
  organizationID?: string
  status?: string
}

const toOptionRowLike = (value: unknown): OptionRowLike => {
  if (!isRecord(value)) return {}
  return {
    id: typeof value.id === 'string' ? value.id : undefined,
    userID: typeof value.userID === 'string' ? value.userID : undefined,
    organizationID:
      typeof value.organizationID === 'string'
        ? value.organizationID
        : undefined,
    status: typeof value.status === 'string' ? value.status : undefined,
  }
}

export const resolveOptionPermissions = ({
  context,
  node,
  data,
}: OptionPermissionInput): OptionPermissions => {
  const contextRecord = isRecord(context) ? context : {}
  const authorization = isRecord(contextRecord.authorization)
    ? contextRecord.authorization
    : {}
  const optionsAuth = isRecord(authorization.optionsAuth)
    ? authorization.optionsAuth
    : {}
  const user = isRecord(authorization.user) ? authorization.user : {}
  const team = isRecord(contextRecord.team) ? contextRecord.team : {}
  const ownership = isRecord(contextRecord.ownership)
    ? contextRecord.ownership
    : {}

  const currentUserId =
    typeof user.id === 'string'
      ? user.id
      : typeof contextRecord.user === 'object' &&
          contextRecord.user &&
          typeof (contextRecord.user as { id?: unknown }).id === 'string'
        ? ((contextRecord.user as { id: string }).id ?? undefined)
        : typeof ownership.byUserID === 'string'
          ? ownership.byUserID
          : undefined
  const contextScope = isRecord(contextRecord.organizationScope)
    ? contextRecord.organizationScope
    : {}
  const activeOrganizationId =
    typeof contextScope.activeOrganizationId === 'string'
      ? contextScope.activeOrganizationId
      : resolveActiveOrganizationId({
          ownership,
          team,
        })
  const scopeKind =
    contextScope.scopeKind === 'organization' ||
    contextScope.scopeKind === 'solo' ||
    contextScope.scopeKind === 'unresolved'
      ? contextScope.scopeKind
      : 'unresolved'
  const inheritedOrganizationIds = resolveInheritedOrganizationIds({
    scopeKind,
    activeOrganizationId,
    activeOrganizationMasterId:
      typeof contextScope.activeOrganizationMasterId === 'string'
        ? contextScope.activeOrganizationMasterId
        : undefined,
    selectedOrganizationParent:
      isRecord(contextScope.selectedOrganizationParent) &&
      typeof contextScope.selectedOrganizationParent.id === 'string'
        ? { id: contextScope.selectedOrganizationParent.id }
        : undefined,
  })
  const organizationFamilyIds = Array.isArray(
    contextRecord.organizationFamilyIds,
  )
    ? contextRecord.organizationFamilyIds.filter(
        (organizationId): organizationId is string =>
          typeof organizationId === 'string' && organizationId.length > 0,
      )
    : [activeOrganizationId, ...inheritedOrganizationIds].filter(
        (organizationId): organizationId is string =>
          typeof organizationId === 'string' && organizationId.length > 0,
      )
  const isGuest = user.role === 'GUEST'
  const isListOwner = Boolean(contextRecord.isListOwner)
  const isListOwnerResolved = Boolean(contextRecord.isListOwnerResolved)
  const optionAccessPolicy = isRecord(contextRecord.optionAccessPolicy)
    ? contextRecord.optionAccessPolicy
    : {}

  const rowFromData = toOptionRowLike(data)
  const rowFromNode = toOptionRowLike(node?.data)
  const rowHasInlineData = Boolean(
    rowFromData.id || rowFromData.userID || rowFromData.organizationID,
  )
  const row = rowHasInlineData ? rowFromData : rowFromNode

  const isGroupHeader = Boolean(node?.group && !row.id)
  const isNewOption = Boolean(row.id?.startsWith('new-'))
  const isOwner = Boolean(
    row.userID && currentUserId && row.userID === currentUserId,
  )
  const isSameOrganization = Boolean(
    row.organizationID &&
    activeOrganizationId &&
    row.organizationID === activeOrganizationId,
  )

  const { canManageList } = resolveOptionListAccess({
    canUpdate: !isGuest && Boolean(optionsAuth.canUpdate),
    isListOwner,
    isListOwnerResolved,
  })
  const canCreate =
    Boolean(optionAccessPolicy.canCreateInList) ||
    (Boolean(optionsAuth.canCreate) &&
      !isGuest &&
      (canManageList ||
        (scopeKind === 'organization' &&
          isListOwnerResolved &&
          !isListOwner &&
          typeof contextRecord.listOrganizationID === 'string')))
  const canUpdate = Boolean(optionsAuth.canUpdate) && canManageList
  const canDelete = Boolean(optionsAuth.canDelete) && canManageList
  const canEditExisting =
    canUpdate ||
    (Boolean(optionsAuth.canUpdate) &&
      !isGuest &&
      canEditSharedOptionDraft({
        optionOrganizationID: row.organizationID,
        optionUserID: row.userID,
        optionStatus: row.status,
        organizationFamilyIDs: organizationFamilyIds,
        currentUserID: currentUserId,
        scopeKind,
      }))
  const canDeleteExisting = canDelete

  return {
    isGuest,
    isOwner,
    isSameOrganization,
    isListOwner,
    isListOwnerResolved,
    isNewOption,
    isGroupHeader,
    canCreate,
    canUpdate,
    canDelete,
    canEditExisting,
    canDeleteExisting,
    canUnarchive:
      contextRecord.isAdmin === true && canUpdate && row.status === 'ARCHIVED',
  }
}

type OptionEditableParams =
  | CellClassParams<OptionRow>
  | EditableCallbackParams<OptionRow>
  | RowClassParams<OptionRow>

export const isOptionEditable = (params: OptionEditableParams): boolean => {
  const contextRecord = isRecord(params.context) ? params.context : {}
  if (contextRecord.isHistoryView === true) return false

  const permissions = resolveOptionPermissions(params)

  if (permissions.isGuest || permissions.isGroupHeader) return false
  if (permissions.isNewOption) return permissions.canCreate
  return permissions.canEditExisting
}

export const resolveOptionListAccess = (params: {
  canUpdate: boolean
  isListOwner: boolean
  isListOwnerResolved?: boolean
}): OptionListAccess => {
  const { canUpdate, isListOwner, isListOwnerResolved = true } = params

  if (!canUpdate) {
    return {
      canManageList: false,
      restrictionReason: 'missing-update-right',
    }
  }

  if (!isListOwnerResolved) {
    return {
      canManageList: false,
      restrictionReason: 'owner-unresolved',
    }
  }

  if (!isListOwner) {
    return {
      canManageList: false,
      restrictionReason: 'not-list-owner',
    }
  }

  return {
    canManageList: true,
    restrictionReason: null,
  }
}
